import type { AxiosRequestConfig } from 'axios';
import { type ErrorLogEntry } from '../support/logs/ErrorLog';
import { type Notification } from '../support/notifications/Notifications';
import axios from './Axios';
import { getAxiosHeader, urlDataRelease, urlSRelease, urlServicesRelease, urlSupportRelease } from './AxiosVars';
import { getIdFirma, getIdUser } from './Session';
import { checkUndefinedNullOrEmpty } from './Tools';
import { type InputDateTimeProps } from './inputfields/DateTime';
import { type InputSelectProps } from './inputfields/Select';
import { type InputTextProps } from './inputfields/Text';

type RawErrorLogEntry = Omit<ErrorLogEntry, 'msg'> & {
    msg: string;
    topic: string;
};

export function getErrorLog(
    filter: {
        dateBis?: InputDateTimeProps['v'];
        dateVon?: InputDateTimeProps['v'];
        filter?: string;
        id_firma?: InputTextProps['v'];
        id_user?: InputTextProps['v'];
        id_car?: InputTextProps['v'];
        id_portal?: InputSelectProps['v'];
        id_proto?: InputTextProps['v'];
        level?: InputSelectProps['v'];
        limit?: number;
        quelle?: InputSelectProps['v'];
        status?: InputSelectProps['v'];
    } = {}
) {
    const idFirma = !checkUndefinedNullOrEmpty(filter.id_firma) ? filter.id_firma : -1;
    const idUser = !checkUndefinedNullOrEmpty(filter.id_user) ? filter.id_user : -1;
    const idCar = !checkUndefinedNullOrEmpty(filter.id_car) ? filter.id_car : -1;
    const idPortal = !checkUndefinedNullOrEmpty(filter.id_portal) ? filter.id_portal : -1;
    const idProto = !checkUndefinedNullOrEmpty(filter.id_proto) ? filter.id_proto : -1;
    const level = !checkUndefinedNullOrEmpty(filter.level) ? filter.level : -1;
    const quelle = !checkUndefinedNullOrEmpty(filter.quelle) ? filter.quelle : -1;
    const dateFrom = !checkUndefinedNullOrEmpty(filter.dateVon) ? filter.dateVon : -1;
    const dateTo = !checkUndefinedNullOrEmpty(filter.dateBis) ? filter.dateBis : -1;
    const limit = !checkUndefinedNullOrEmpty(filter.limit) ? filter.limit : -1;
    const status = !checkUndefinedNullOrEmpty(filter.status) ? filter.status : -1;

    return axios<RawErrorLogEntry[]>({
        title: 'Abrufen der Fehler-Logs',
        method: 'post',
        url: `${urlDataRelease}/log/v1/search/${idFirma}/${idUser}/${idCar}/${idPortal}/${idProto}/${level}/${quelle}/${dateFrom}/${dateTo}/${limit}/${status}`,
        data: filter?.filter || undefined,
        headers: getAxiosHeader(),
    });
}

export function getFirmenForVinNachweis(jahr: string, monat: string) {
    return axios<number[]>({
        title: 'Abrufen der Firmen mit Nachweisen',
        method: 'get',
        url: `${urlServicesRelease}/proto1/v1/dat/firmen/${jahr}/${monat}`,
        headers: getAxiosHeader(),
    });
}

export function getNachweisForFirma(idFirma: number, jahr: string, monat: string, config: AxiosRequestConfig) {
    return axios<{ user: string; typ: string; date: string; code: string }[]>({
        title: 'Abrufen der Nachweise der Firma ' + idFirma,
        method: 'get',
        url: `${urlServicesRelease}/proto1/v1/dat/firma/${idFirma}/${jahr}/${monat}`,
        headers: getAxiosHeader(),
        ...config,
    });
}

// Notifications
export function getNotificationsValid() {
    return axios<Notification[]>({
        title: 'Abrufen aller globalen Benachrichtigungen',
        method: 'get',
        url: `${urlServicesRelease}/firma3/v1/info/valid`,
        headers: getAxiosHeader(),
    });
}

export function getNotificationsOffen() {
    return axios<Notification[]>({
        title: 'Abrufen aller aktiven globalen Benachrichtigungen',
        method: 'get',
        url: `${urlServicesRelease}/firma3/v1/info/open`,
    });
}

export function newNotification(data) {
    return axios({
        title: 'Erstellen der globalen Benachrichtigungen',
        method: 'post',
        url: `${urlServicesRelease}/firma3/v1/info`,
        data: data,
        headers: getAxiosHeader(),
    });
}

/*
 * Lead als Spam  markieren
 */
export function setLeadSpam(idLead) {
    const url = urlSRelease + '/support/spam/' + getIdFirma() + '/' + getIdUser() + '/' + idLead;
    return axios({
        title: 'Spam melden',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

/*
 *  den Lead einen Kunden zuzuweisen
 */
export function setClientToLead(idLead, idClient) {
    const url = urlSRelease + '/support/connectKunde/' + getIdFirma() + '/' + getIdUser() + '/' + idLead + '/' + idClient;
    return axios({
        title: 'Zuweisen des Kunden',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

/*
 * Ein Lead an die Geschäftsführung oder Vertrieb weiterleiten
 */
export function weiterleitenUnbekannt(idLead, param) {
    const url = urlSRelease + '/support/weiterleiten/' + getIdFirma() + '/' + getIdUser() + '/' + idLead + '/' + param;
    return axios({
        title: 'Weiterleiten',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function saveConfigLead(idFirma, idUser, idFirmaConfig, strData) {
    const url = urlSupportRelease + '/s/firma/setConfigGlobal/' + idFirma + '/' + idUser + '/' + idFirmaConfig;
    return axios({
        title: 'Speichern der Lead-Einstellungen',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
        data: strData,
    });
}
